import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, MapPin, Bell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const devErr = process.env.NODE_ENV !== 'production' ? console.error.bind(console) : () => {};

export default function Profile() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', phone: '', shipping_address: {} });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/auth'); return; }
    setForm({
      name: user.name || '',
      phone: user.phone || '',
      shipping_address: user.shipping_address || {},
    });
  }, [user, authLoading]);

  const save = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/profile`, form, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { /* silently handle fetch error */
      devErr('[Profile] save failed');
    }
    finally { setLoading(false); }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (!user) return null;

  return (
    <div className="bg-white min-h-screen pt-20" data-testid="profile-page">
      <div className="max-w-2xl mx-auto px-6 lg:px-12 py-12">
        <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3 font-work">My Account</p>
        <h1 className="font-playfair text-4xl text-[#0A0A0A] mb-12">Profile</h1>

        {/* Avatar */}
        <div className="flex items-center gap-6 mb-12 pb-12 border-b border-[#E5E5E5]">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-full object-cover" />
          ) : (
            <div className="w-20 h-20 bg-[#F9F9F9] flex items-center justify-center">
              <User size={32} className="text-[#CCCCCC]" />
            </div>
          )}
          <div>
            <p className="font-playfair text-2xl text-[#0A0A0A]">{user.name}</p>
            <p className="text-sm text-[#999999] font-work mt-1">{user.email}</p>
            <p className="text-xs uppercase tracking-wider text-[#D4AF37] font-work mt-2">{user.role === 'admin' ? 'Personal Shopper' : 'Member'}</p>
          </div>
        </div>

        {/* Form */}
        <div className="flex flex-col gap-6">
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#0A0A0A] font-work block mb-2">Full Name</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full border border-[#E5E5E5] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm font-work text-[#0A0A0A]"
              data-testid="profile-name-input"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#0A0A0A] font-work block mb-2">Phone</label>
            <input
              type="tel"
              value={form.phone || ''}
              onChange={e => setForm({ ...form, phone: e.target.value })}
              placeholder="+27 XX XXX XXXX"
              className="w-full border border-[#E5E5E5] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm font-work text-[#0A0A0A]"
              data-testid="profile-phone-input"
            />
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#0A0A0A] font-work mb-4 flex items-center gap-2">
              <MapPin size={14} /> Shipping Address
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { key: 'street', label: 'Street Address', cols: 2 },
                { key: 'city', label: 'City' },
                { key: 'country', label: 'Country' },
              ].map(f => (
                <div key={f.key} className={f.cols === 2 ? 'sm:col-span-2' : ''}>
                  <label className="text-xs text-[#999999] font-work block mb-1">{f.label}</label>
                  <input
                    type="text"
                    value={form.shipping_address?.[f.key] || ''}
                    onChange={e => setForm({ ...form, shipping_address: { ...form.shipping_address, [f.key]: e.target.value } })}
                    className="w-full border border-[#E5E5E5] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm font-work text-[#0A0A0A]"
                  />
                </div>
              ))}
            </div>
          </div>

          {saved && (
            <div className="bg-[#2E7D32]/10 border border-[#2E7D32]/20 px-4 py-3 text-sm text-[#2E7D32] font-work" data-testid="profile-saved-msg">
              Profile saved successfully
            </div>
          )}

          <div className="flex gap-4 pt-4">
            <button
              onClick={save}
              disabled={loading}
              className="flex-1 bg-[#0A0A0A] text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors py-4 text-xs uppercase tracking-widest font-semibold font-work disabled:opacity-50"
              data-testid="save-profile-btn"
            >
              {loading ? 'Saving...' : 'Save Profile'}
            </button>
            <button
              onClick={handleLogout}
              className="border border-[#E5E5E5] text-[#0A0A0A] hover:border-[#D32F2F] hover:text-[#D32F2F] transition-colors px-8 py-4 text-xs uppercase tracking-widest font-work"
              data-testid="logout-btn"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
